import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import Header from '../components/header';
import logo from '../img/logo-softmining.svg';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { FaArrowRight } from 'react-icons/fa';
import reportchatbotIcon from '../img/reportchatbotIcon.jpg';
import { AiOutlineCloudUpload } from 'react-icons/ai';
import { useAuth } from "../context/AuthContext";

import { retrieveMineData, consumeToken } from '../Utility/ConsumeToken/consumeToken';
import {
  enqueueDeepsageRequest,
  checkDeepsageStatus,
  getDeepsageOutput,
} from '../Utility/Deepsage/deepsageUtils';

import ReportChat from '../components/ReportChat';
import { useGID } from '../context/GIDContext';
import htmlToPdfMake from 'html-to-pdfmake';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { navigateToNextPage } from "../Utility/CreatePipeline/createPipelineUtils";

/* === IMPORT per Vizit Storage === */
import VizitStorageLoader from '../components/VizitStorageLoader';

/* Alcune build espongono vfs su pdfFonts.pdfMake.vfs, altre su pdfFonts.vfs */
pdfMake.vfs = pdfFonts.pdfMake?.vfs || pdfFonts.vfs;

/* ====== UI helpers ====== */

function FloatingChatInvitation({ onClick, text }) {
  return (
    <div className="floating-chat-invitation" onClick={onClick}>
      <img src={reportchatbotIcon} alt="ChatBot" className="floating-chat-icon" />
      <div className="floating-chat-invitation-inner" onClick={onClick}>
        <span>{text}</span>
      </div>
    </div>
  );
}

const sortByDateDesc = (arr) =>
  [...arr].sort((a, b) => {
    const ta = a?.date ? new Date(a.date).getTime() : 0;
    const tb = b?.date ? new Date(b.date).getTime() : 0;
    return tb - ta;
  });

/* ricava un gid safe da un filename (solo alfanumerico, min 5 char se possibile) */
const gidFromFilename = (filename = '') => {
  const base = filename.split('.').slice(0, -1).join('.') || filename;
  const safe = (base || '').replace(/[^A-Za-z0-9]/g, '');
  if (safe.length >= 5) return safe;
  // fallback: pad con numeri se troppo corto
  return (safe + '12345').slice(0, Math.max(5, safe.length));
};

const Deepsage = () => {
  const [gid, setgid] = useState('');
  const [inputJson, setInputJson] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [outputMarkdown, setOutputMarkdown] = useState('');
  const [file, setFile] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const outputRef = useRef(null);
  const [fontSize, setFontSize] = useState(16);
  const [searchTerm, setSearchTerm] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { setGid } = useGID();

  const { user } = useAuth();
  const isAdmin = user?.data?.admin === true;

  // Stato overlay Report Assistant
  const [showReportChat, setShowReportChat] = useState(false);

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  /* ====== Effects iniziali ====== */
  useEffect(() => {
    // se arriva un report già pronto
    if (location.state?.params?.reportContent) {
      setOutputMarkdown(location.state.params.reportContent);
      setShowReport(true);
    }
    // se abbiamo dati di pipeline, usali come inputJson
    if (!inputJson && location.state?.params?.pipelineData) {
      setInputJson(location.state.params.pipelineData);
    }
  }, [location.state, inputJson]);

  const proceedPipeline = () => {
    if (!location.state) return;
    const { params = {}, board = [], nextPageIndex = 0 } = location.state;
    const newParams = { ...params, pipelineData: outputMarkdown };
    navigateToNextPage(nextPageIndex, newParams, board, navigate);
  };

  /* ====== Submit locale/da pipeline ====== */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setStatusMessage('');
    setOutputMarkdown('');

    if (!inputJson) {
      // Fallback nel caso in cui, per qualsiasi motivo, non ci fosse inputJson
      const fileInput = document.getElementById('fileInput');
      const file = fileInput?.files[0];
      if (!file) {
        alert("Please select a JSON file before submitting.");
        return;
      }
      try {
        const fileContent = await readFileAsJson(file);
        setInputJson(fileContent);
        // se manca gid, prova dal nome file
        if (!gid || gid.length < 5) {
          setgid(gidFromFilename(file.name));
        }
        startDeepsage(fileContent, gid || gidFromFilename(file.name));
      } catch (err) {
        alert("Error parsing the JSON file.");
      }
    } else {
      // caso normale: inputJson già selezionato (da Vizit o da upload locale)
      startDeepsage(inputJson, gid && gid.length >= 5 ? gid : undefined);
    }
  };

  const readFileAsJson = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const jsonData = JSON.parse(event.target.result);
          resolve(jsonData);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = (error) => reject(error);
      reader.readAsText(file);
    });
  };

  /* === NUOVO: gestione upload locale admin (imposta subito inputJson) === */
  const handleLocalFileChange = (e) => {
    const uploadedFile = e.target.files && e.target.files[0];
    setFile(uploadedFile || null);

    if (!uploadedFile) {
      setInputJson(null);
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const parsed = JSON.parse(event.target.result);
        // Imposta l'inputJson così si passa alla schermata "Project Name"
        setInputJson(parsed);

        // Suggerisci il gid dal nome file, se non già valido
        setgid((prev) => {
          if (prev && prev.length >= 5) return prev;
          if (uploadedFile.name) {
            return gidFromFilename(uploadedFile.name);
          }
          return prev || '';
        });
      } catch (err) {
        console.error(err);
        alert("Error parsing the JSON file. Please check its content.");
        setInputJson(null);
      }
    };
    reader.onerror = () => {
      alert("Error reading the file.");
      setInputJson(null);
    };
    reader.readAsText(uploadedFile);
  };

  /* === NUOVO: reset completo dell'inputJson per tornare alla schermata precedente === */
  const handleResetInput = () => {
    setInputJson(null);
    setgid('');
    setFile(null);
    setOutputMarkdown('');
    setError(null);
  };

  const startDeepsage = async (jsonData, fileName) => {
    setShowModal(true);
    setLoading(true);
    setStatusMessage("Sending Deepsage request...");

    try {
      const apitoken = await retrieveMineData();
      if (apitoken.tokens > 9) {
        await consumeToken(-10);
        setStatusMessage("Request queued. Checking status...");
        const enqueueResult = await enqueueDeepsageRequest(apitoken._id, JSON.stringify(jsonData), fileName);
        let completed = false;
        while (!completed) {
          await new Promise(resolve => setTimeout(resolve, 3000));
          const statusResult = await checkDeepsageStatus(apitoken._id, enqueueResult.GID);
          if (statusResult.output === "REQUEST COMPLETED") {
            setStatusMessage("Processing completed. Retrieving output...");
            completed = true;
          } else {
            setStatusMessage(`Current status: ${statusResult.output}`);
          }
        }
        const outputResult = await getDeepsageOutput(apitoken._id, enqueueResult.GID);
        setGid(enqueueResult.GID);
        let markdownText = "";
        if (outputResult && outputResult.output) {
          markdownText = outputResult.output;
        }
        setOutputMarkdown(markdownText);
        setStatusMessage("Output retrieved.");
      } else {
        alert("Insufficient token");
      }
    } catch (err) {
      setError(err.message);
      setStatusMessage("");
    } finally {
      setLoading(false);
    }
  };

  const handleViewOutput = () => {
    setShowModal(false);
    setShowReport(true);
    setTimeout(() => {
      if (outputRef.current) {
        outputRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const downloadPDF = async () => {
    if (!outputRef.current) return;

    const clone = outputRef.current.cloneNode(true);
    clone.querySelectorAll('img, table').forEach(el => {
      el.style.maxWidth = '100%';
      el.style.height = 'auto';
    });

    const toDataURL = (img) => new Promise((resolve) => {
      const i = new Image();
      i.crossOrigin = 'anonymous';
      i.onload = () => {
        try {
          const c = document.createElement('canvas');
          c.width = i.naturalWidth; c.height = i.naturalHeight;
          const ctx = c.getContext('2d');
          ctx.drawImage(i, 0, 0);
          resolve(c.toDataURL('image/png'));
        } catch {
          resolve(img.src);
        }
      };
      i.onerror = () => resolve(img.src);
      i.src = img.src;
    });

    const imgs = Array.from(clone.querySelectorAll('img'));
    if (imgs.length) {
      const srcs = await Promise.all(imgs.map(toDataURL));
      imgs.forEach((img, idx) => img.setAttribute('src', srcs[idx]));
    }

    const html = clone.innerHTML;

    let content = htmlToPdfMake(html, {
      defaultStyles: {
        p: { fontSize: 12, lineHeight: 1.25, margin: [0, 4, 0, 4] },
        li: { fontSize: 12, lineHeight: 1.25, margin: [0, 2, 0, 2] },
        h1: { fontSize: 20, bold: true, margin: [0, 10, 0, 6] },
        h2: { fontSize: 16, bold: true, margin: [0, 8, 0, 4] },
        h3: { fontSize: 14, bold: true, margin: [0, 6, 0, 4] },
        pre: { fontSize: 11, margin: [0, 6, 0, 6] },
        table: { margin: [0, 6, 0, 6] }
      }
    });

    const markUnbreakable = (node) => {
      if (Array.isArray(node)) return node.map(markUnbreakable);
      if (node && typeof node === 'object') {
        const tag = node.nodeName || node._nodeName;
        if (['P', 'LI', 'BLOCKQUOTE', 'PRE'].includes(tag)) {
          return { unbreakable: true, stack: [{ ...node }] };
        }
        if (node.table) {
          node.layout = node.layout || 'lightHorizontalLines';
          node.dontBreakRows = true;
          node.keepWithHeaderRows = 1;
        }
        if (node.stack) node.stack = markUnbreakable(node.stack);
        if (node.columns) node.columns = markUnbreakable(node.columns);
        if (node.ul) node.ul = markUnbreakable(node.ul);
        if (node.ol) node.ol = markUnbreakable(node.ol);
      }
      return node;
    };
    content = markUnbreakable(content);

    const docDefinition = {
      pageSize: 'A4',
      pageMargins: [24, 28, 24, 28],
      defaultStyle: { color: '#111' },
      content
    };

    pdfMake.createPdf(docDefinition).download('deepsage_output.pdf');
  };

  const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const getHighlightedMarkdown = () => {
    if (!searchTerm) return outputMarkdown;
    const regex = new RegExp(escapeRegExp(searchTerm), 'gi');
    return outputMarkdown.replace(regex, '<mark>$&</mark>');
  };

  // Responsive sidebar behavior
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1100) {
        setIsSidebarOpen(true);   // su desktop sidebar sempre aperta
      } else {
        setIsSidebarOpen(false);  // su mobile parte chiusa
      }
    };

    handleResize(); // inizializza in base alla larghezza attuale
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /* ====== RENDER ====== */

  if (showReport) {
    return (
      <>
        <Header />
        <div className="riga headerTools">
          <h3 className="titolo titoloFeature">DEEPSAGE</h3>
          <span className="text description descriptionSidebarTools">
            DeepSage goes beyond traditional bibliographic searches, transforming raw data into actionable insights. Instead of merely compiling article lists or graphs, it applies AI-driven analysis to detect trends, evaluate research impact, and uncover hidden connections in scientific literature. DeepSage helps researchers make informed decisions by providing a deeper contextual understanding of their field.
          </span>
          <div className="space"></div>
          <button
            type="button"
            className="button sign-button sidebar-toggle"
            onClick={() => setIsSidebarOpen(prev => !prev)}
          >
            {isSidebarOpen ? 'Hide settings' : 'Show settings'}
          </button>
        </div>
        <div className="bodysidebar-layout">
          <div className={`ManageAreaSidebar ${isSidebarOpen ? 'is-open' : ''}`}>
            <h2 className="titolo TitleSidebar">
              <center>VIEW SETTINGS</center>
            </h2>
            <div className="colonna4">
              <label className="textlink labelcss" htmlFor="searchInput">Search Text:</label>
              <input
                type="text"
                id="searchInput"
                placeholder="Enter search term..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <div className="spaceGraph"></div>
            </div>
            <div className="colonna4">
              <label className="textlink labelcss" htmlFor="fontSizeRange">Text Size</label>
              <input
                type="range"
                id="fontSizeRange"
                min="10"
                max="30"
                value={fontSize}
                onChange={(e) => setFontSize(e.target.value)}
              />
              <div className="spaceGraph"></div>
              <div className="spaceGraph"></div>
              <div className="spaceGraph"></div>
            </div>
            <button className="sign-button button" onClick={downloadPDF}>
              Export PDF
            </button>
            <hr className="separatorSidebar"></hr>
            <span className="text descriptionSidebar text-color-dark">
              <b>Analyze your report</b> and instantly obtain all the information you need with <b>DeepSage Report Assistant.</b>
            </span>
            <FloatingChatInvitation onClick={() => setShowReportChat(!showReportChat)} text={showReportChat ? "Close Report Assistant" : "Open Report Assistant"} />
            <div className="riga">
              <div className='space'></div>
              <Link className="textlink textLinkSign" to="/dashboard">
                Back to Dashboard <FaArrowRight size={12} style={{ marginLeft: '8px', marginBottom: '-1px' }} />
              </Link>
            </div>
          </div>

          <div
            className="deepsage-body-sidebar"
            id="mynetwork"
            style={{ fontSize: `${fontSize}px` }}
          >
            <div className="deepsage-output-container" ref={outputRef}>
              <div className="markdown-viewer">
                <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                  {getHighlightedMarkdown()}
                </ReactMarkdown>
              </div>
            </div>
            <div className={`report-chat-sidebar ${showReportChat ? 'open' : ''}`}>
              <ReportChat isOpen={true} onClose={() => setShowReportChat(false)} />
            </div>
          </div>
          <div className="space"></div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="riga headerTools">
        <h3 className="titolo titoloFeature">DeepSage</h3>
        <p className="text description">
          DeepSage goes beyond traditional bibliographic searches, transforming raw data into actionable insights. Instead of merely compiling article lists or graphs, it applies AI-driven analysis to detect trends, evaluate research impact, and uncover hidden connections in scientific literature. DeepSage helps researchers make informed decisions by providing a deeper contextual understanding of their field.
        </p>
        <div className="space"></div>
      </div>
      <center>
        {inputJson && (
          <>
            <div className="form formLogin formTools">
              <img src={logo} className="sign-logo" alt="logo" />
              <form onSubmit={handleSubmit}>
                <label htmlFor="gid">Project Name:</label>
                <input
                  placeholder="Enter only letters and numbers..."
                  type="text"
                  id="gid"
                  name="gid"
                  value={gid}
                  onChange={e => setgid(e.target.value)}
                  pattern="[A-Za-z0-9]{5,}"
                  title="Only letters and numbers (no space or special characters), at least 5 characters."
                  minLength={5}
                  required
                />
                <button className="sign-button button" type="submit" disabled={loading}>
                  Start DeepSage
                </button>

                <p className="text noMarginTop">- or -</p>

                <button
                  type="button"
                  className="download-button sign-button button"
                  onClick={handleResetInput}
                >
                  Change input
                </button>

                <div className='spaceGraph'></div>

                <div className="riga">
                  <Link className="textlink textLinkSign" to="/dashboard">
                    Back to Dashboard <FaArrowRight size={12} style={{ marginLeft: '8px', marginBottom: '-1px' }} />
                  </Link>
                </div>
                {error && <p style={{ color: "red" }}>Error: {error}</p>}
              </form>
            </div>
            
            <div className="space"></div>
          </>
        )}

        {!inputJson && (
          <>
            <VizitStorageLoader
              buttonClassName="sign-button button download-button"
              buttonLabel="Upload from Vizit Storage"
              textAction="Use in Deepsage"
              popupMaxWidth={1000}
              isAdmin={isAdmin}
              onLoad={(parsed, fileName) => {
                setInputJson(parsed);

                setgid(prev => {
                  // Se l'utente aveva già scritto qualcosa di valido, NON toccare
                  if (prev && prev.length >= 5) return prev;

                  // Se arriva un filename da Vizit, prova a usarlo
                  if (fileName) {
                    return gidFromFilename(fileName);
                  }

                  // Altrimenti non forzare nulla: lascia com'è
                  // (l'utente dovrà comunque compilare il campo, che è required)
                  return prev || '';
                });
              }}
            />
            
            <div className="spaceGraph"></div>
            <div className="spaceGraph"></div>

            {isAdmin && (
              <>
                <p>- or -</p>
                <form className='uploadForm'>
                  <div className="file-upload-container">
                    <label htmlFor="fileInput" className="custom-file-label-upload textlink">
                      <AiOutlineCloudUpload className="upload-icon-35px textlink" />
                      {!file ? (
                        <span className="noBottomSpace">Upload a JSON file from your computer</span>
                      ) : (
                        <span className="noBottomSpace">{file.name}</span>
                      )}
                    </label>
                    <input
                      type="file"
                      id="fileInput"
                      accept=".json"
                      onChange={handleLocalFileChange} 
                      style={{ display: 'none' }}
                      required
                    />
                  </div>
                </form>
                
                <div className="spaceGraph"></div>
              </>
            )}
          </>
        )}
      </center>

      {/* === Popup processo Deepsage === */}
      {showModal && (
        <div className="popup-overlay">
          <div className="popup-content">
            <div className="divCloseButton">
              <button onClick={() => setShowModal(false)} className="CloseButton text">
                X
              </button>
            </div>
            <h4 className="popupTitle titolo">DeepSage Process</h4>
            {loading && (
              <>
                <p>Please wait, the process is running...</p>
                <p>
                  You can close this popup and navigate to another screen, as the process will continue to run in the background.
                </p>
              </>
            )}
            {!loading && outputMarkdown && (
              <>
                <p>Process completed! The resulting files have been saved on the DeepSage Storage page.</p>
                {location.state && location.state.nextPageIndex !== undefined ? (
                  <>
                    <button className="sign-button button" onClick={proceedPipeline}>
                      Proceed to next feature
                    </button>
                    <button className="sign-button button" onClick={() => navigate('/DeepsageStorage')}>
                      Go to DeepSage Storage
                    </button>
                  </>
                ) : (
                  <>
                    <button className="sign-button button" onClick={handleViewOutput}>
                      View Output
                    </button>
                    <button className="sign-button button" onClick={() => navigate('/DeepsageStorage')}>
                      Go to DeepSage Storage
                    </button>
                  </>
                )}
              </>
            )}
            
          </div>
        </div>
      )}
    </>
  )
};

export default Deepsage;
